import React, { useState, useEffect } from 'react';
import { ContentHeader } from '@components';
import Select from 'react-select';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import makeAnimated from 'react-select/animated';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { Button, Modal } from 'react-bootstrap';
import Editor from '../../components/text-editor';
import { uploadImage, getFileExtentions } from '../../services/auth';
import { getUserPermission } from '../../utils/helpers';
import { updateDocAsset, createDoc, createDocument } from '../../services/docs';
import { getAllProjectNames } from '../../services/project';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '@app/store/reducers/auth';
import handleStatus from '../../utils/ajax';
const animatedComponents = makeAnimated();
import { BsImage } from 'react-icons/bs';
import { filesDataCreator } from '../../services/docs';
import ViewDocFile from './ViewDocFile';
import { MdOutlineClose, MdImage, MdGif, MdAudioFile } from 'react-icons/md';
import {
  BsFiletypeDocx,
  BsFileZip,
  BsFiletypeCsv,
  BsFileExcel
} from 'react-icons/bs';
import { TbPdf } from 'react-icons/tb';

const CreateDoc = () => {
  const [doc, setDoc] = useState('');
  const [docName, setDocName] = useState('');
  const [allProject, setAllProject] = useState([]);
  const [project, setProject] = useState({});
  const [show, setShow] = useState(false);
  const [activeLink, setActiveLink] = useState('');
  const [file, setFile] = useState(null);
  const [hash, setHash] = useState('');
  const [uploaded, setUploaded] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const { projectId } = params;
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth.currentUser);

  function ModalPopup({ setHash }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleClose = () => {
      setShow(false);
      setActiveLink('');
      setHash('');
    };

    const uploadDoc = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        setShow(false);
        setUploading(true);
        setFile(file);
        setUploaded({});
        const resp = await uploadImage(file);
        handleStatus(resp.status, () => dispatch(logoutUser()), navigate);

        if (resp.status === 200) {
          const { hash, fileName } = resp.data;
          setUploaded({ hash, fileName });
          setActiveLink(window.location.origin + '/file/' + resp.data.hash);
          setHash(resp.data.hash);
          toast('File successfully uploaded');
          setShow(true);
          setUploading(false);
        } else {
          setUploading(false);
          toast.error('Something went wrong while uploading file!');
        }
      }
    };

    return (
      <>
        {uploading ? (
          <label className="btn btn-default">Uploading..</label>
        ) : (
          <label className="btn btn-default mb-0">
            <i className="fas fa-upload mr-2"></i> File Upload{' '}
            <input onChange={uploadDoc} type="file" hidden />
          </label>
        )}

        <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title> heading</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="input-group w-60">
              <input
                type="text"
                className="form-control"
                value={activeLink}
                disabled
              />
              <CopyToClipboard
                text={activeLink}
                onCopy={() => toast('Link Copied!')}
              >
                <button type="button" className="btn btn-secondary">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-clipboard"
                    viewBox="0 0 16 16"
                  >
                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"></path>
                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"></path>
                  </svg>
                </button>
              </CopyToClipboard>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleClose}>
              close
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  useEffect(() => {
    if (Object.keys(uploaded).length > 0) {
      setUploadedFiles((prev) => [...prev, uploaded]);
    }
  }, [uploaded]);

  const onSubmit = async (values: any) => {
    if (docName === '') {
      toast.error('Please enter document name');
    }

    if (doc === '') {
      toast.error('Please enter content in the doc');
      return;
    }

    const userRole =
      getUserPermission(user) === 'admin'
        ? false
        : (getUserPermission(user) === 'developer' || getUserPermission(user) === 'seo')
          ? true
          : null;

    const savedDoc = await createDoc(docName, doc, projectId, userRole);
    if (!savedDoc?.data?.ok) {
      toast.warn(savedDoc?.data?.message || 'Error while creating documentation')
      return
    }
    handleStatus(savedDoc.status, () => dispatch(logoutUser()), navigate);
    
    if (savedDoc.status === 200 && uploadedFiles.length === 0) {
      toast.success('Document created successfully...') &&
        navigate(`/view-project/${projectId}`);
    }

    if (savedDoc.status === 200 && uploadedFiles.length > 0) {
      const { _id: documentationId, projectId } = savedDoc.data.data;
      const filesData = filesDataCreator(
        uploadedFiles,
        projectId,
        documentationId
      );

      const allPromises = await Promise.all(filesData);
      const allSuccesseded = allPromises.filter(
        (promise) => promise.status !== 200
      );

      if (allSuccesseded.length === 0) {
        toast.success('Document created successfully...') &&
          navigate(`/view-project/${projectId}`);
      }
      if (allSuccesseded.length > 0) {
        toast.error('Something went wrong.');
      }
    }
  };
  const formik = useFormik({
    initialValues: {},
    onSubmit
  });

  const getIcon = (file: any) => {
    if (file === 'png' || file === 'jpg' || file === 'jpeg') {
      return (
        <MdImage
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }

    if (file === 'gif') {
      return (
        <MdGif
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }

    if (file === 'mp3') {
      return (
        <MdAudioFile
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }

    if (file === 'pm4' || file === 'pdf') {
      return (
        <TbPdf
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }

    if (file === 'xls') {
      return (
        <BsFileExcel
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }

    if (file === 'csv') {
      return (
        <BsFiletypeCsv
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }

    if (file === 'zip') {
      return (
        <BsFileZip
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }

    if (file === 'docx') {
      return (
        <BsFiletypeDocx
          style={{
            height: '100px',
            width: '100px',
            margin: '8px',
            color: '#17a2b8'
          }}
        />
      );
    }
  };

  const removeHandler = (id: any) => {
    const allFiles = [...uploadedFiles];
    const filteredFiles = allFiles.filter((item, idx) => idx !== id);
    setUploadedFiles(filteredFiles);
    toast.success('Removed successfully...');
  };

  return (
    <React.Fragment>
      <ContentHeader title="Create Documentation" path={`/view-project/${projectId}`} />
      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Create Documentation</h3>
            </div>
            <div className="card-body">
              <form onSubmit={formik.handleSubmit}>
                <div className="col-12">
                  <div className="card">
                    <div className="py-4 px-4 padding left d-flex align-items-baseline justify-content-between">
                      <h3 className="card-title card-width">Create</h3>
                      <div className="offset-4">
                        <ModalPopup
                          setHash={setHash}
                          hash={hash}
                          activeLink={activeLink}
                          setActiveLink={setActiveLink}
                        />
                      </div>
                    </div>

                    <section className="content">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="card-body">
                            <div className="form-group row">
                              <label
                                htmlFor="documentName"
                                className="col-sm-2 col-form-label"
                              >
                                Document Name
                              </label>
                              <div className="col-sm-6">
                                <input
                                  name="docName"
                                  type="text"
                                  className="form-control"
                                  id="documentName"
                                  placeholder="Document Name"
                                  onChange={(e) => setDocName(e.target.value)}
                                  value={docName}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <div className="docs-section">
                                <Editor value={''} onChange={setDoc} />
                              </div>
                            </div>
                            <div className="d-flex  flex-column">
                              <p>Attachments</p>

                              <div className="d-flex flex-row flex-wrap">
                                {uploadedFiles.map((file, idx) => (
                                  <div key={idx} className="flex flex-column">
                                    {getIcon(file.fileName.split('.')[1])}
                                    <span className="d-flex justify-content-center align-items-center">
                                      <MdOutlineClose
                                        onClick={() => removeHandler(idx)}
                                        style={{
                                          height: '25px',
                                          width: '25px',
                                          margin: '10px'
                                        }}
                                      />
                                      {/* <ViewDocFile fileHash={file.hash} /> */}
                                      {/* <CopyToClipboard
                                        text={file.hash}
                                        onCopy={() => toast('Link Copied!')}
                                      >
                                        <button
                                          type="button"
                                          className="btn btn-secondary"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            fill="currentColor"
                                            className="bi bi-clipboard"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"></path>
                                            <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"></path>
                                          </svg>
                                        </button>
                                      </CopyToClipboard> */}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="form-group row">
                              <button
                                type="submit"
                                disabled={uploading ? true : false}
                                className="btn btn-primary"
                              >
                                Create Document
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};

export default CreateDoc;
